import { Box, Grid, Stack, Typography } from '@mui/material';
import YatungConfirmAlert from '@Src/_basic/components/YatungConfirmAlert';
import YatungDivider from '@Src/_basic/components/YatungDivider';
import YatungInput from '@Src/_basic/components/YatungInput';
import YatungPage from '@Src/_basic/components/YatungPage';
import { GroupData, UserGroupData } from '@Src/_basic/object/AccountManagementType';
import { FactoryData } from '@Src/_basic/object/FactoryType';
import {
  ThirdPartyAccountData,
  ThirdPartyAccountFormValues,
  ThirdPartyApiColumns,
} from '@Src/_basic/object/ThirdPartyAccountType';
import { ThirdPartyAccountApi } from '@Src/_basic/protocol/accountRights/thirdPartyAccount/ThirdPartyAccountApi';
import { FactoryApi } from '@Src/_basic/protocol/factoryManagement/FactoryApi';
import { Form, Formik } from 'formik';
import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import EditAccountField from '../../../_components/EditAccountField';
import EditAccountLabel from '../../../_components/EditAccountLabel';
import EditAccountCreateCard from '../../../AccountManagement/components/EditAccountCreateCard';
import EditAccountMiniTable from '../../../AccountManagement/components/EditAcountForm/components/EditAccountMiniTable';
import ThirdPartyAccountFormActions from '../_components/ThirdPartyAccountFormActions';
import ThirdPartyAccountSearchLimitFields, {
  toSearchLimitFormValues,
  toSearchLimitRequest,
} from '../_components/ThirdPartyAccountSearchLimitFields';
import { editThirdPartyAccountSchema } from '../_components/thirdPartyAccountSchema';
import StateSwitch from './components/StateSwitch';
import ThirdPartyApiColumnSettings from './components/ThirdPartyApiColumnSettings';
import ThirdPartyKeyManagementTable from './components/ThirdPartyKeyManagementTable';

// key 為API路徑, value 為帳號在該API有設定關聯的回傳資料欄位
type ApiColumnSelection = Record<string, Array<string>>;

const isSameColumns = (a: Array<string> = [], b: Array<string> = []) =>
  a.length === b.length && a.every((item) => b.includes(item));

const initialFormValues: ThirdPartyAccountFormValues = {
  account: '',
  accountName: '',
  description: '',
  availableDays: '',
  ...toSearchLimitFormValues(),
};

// 只使用 factoryIds, 其餘欄位為共用 EditAccountCreateCard 所需的型別
const initialUserGroupData: UserGroupData = {
  factoryIds: [],
  groupSettings: [],
  characterSetting: [],
  supervisorReportGroups: [],
  supervisorTransformOrderGroups: [],
  supervisorAssignGroups: [],
};

export default function ThirdPartyAccountUpdateCard() {
  const { t: i18T } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [formValues, setFormValues] = useState<ThirdPartyAccountFormValues>(initialFormValues);
  const [accountInfo, setAccountInfo] = useState<{ createDt: string; updateDt: string }>({
    createDt: '',
    updateDt: '',
  });
  const [state, setState] = useState<string>('1');
  const [token, setToken] = useState<string>('');
  const [tokenLoading, setTokenLoading] = useState<boolean>(false);
  const [isShowAlert, setIsShowAlert] = useState<boolean>(false);
  const [alertText, setAlertText] = useState<string>('');
  const [allFactories, setAllFactories] = useState<Array<FactoryData>>([]);
  const [savedFactoryIds, setSavedFactoryIds] = useState<Array<number>>([]);
  const [isFactoryCreateOpen, setIsFactoryCreateOpen] = useState<boolean>(false);
  // 此功能上線前建立的帳號尚無限制搜尋資料日期的設定, 儲存時需改用 POST 新增
  const [hasSearchLimit, setHasSearchLimit] = useState<boolean>(false);
  const [allApiColumns, setAllApiColumns] = useState<Array<ThirdPartyApiColumns>>([]);
  const [savedApiColumns, setSavedApiColumns] = useState<ApiColumnSelection>({});
  const [apiColumnSelection, setApiColumnSelection] = useState<ApiColumnSelection>({});

  const [userGroupData, setUserGroupData] = useReducer(
    (prev: UserGroupData, next: Partial<UserGroupData>) => ({ ...prev, ...next }),
    initialUserGroupData,
  );

  const validationSchema = useMemo(() => editThirdPartyAccountSchema(i18T), [i18T]);

  const setAccountData = useCallback((_data: ThirdPartyAccountData) => {
    setFormValues((prev) => ({
      ...prev,
      account: _data.account ?? '',
      accountName: _data.accountName ?? '',
      description: _data.description ?? '',
      availableDays: _data.extraData?.availableDays !== undefined ? `${_data.extraData.availableDays}` : '',
    }));
    setAccountInfo({ createDt: _data.createDt ?? '', updateDt: _data.updateDt ?? '' });
    setState(_data.state ?? '1');
    setToken(_data.token ?? '');
    if (_data.account) {
      ThirdPartyAccountApi.getAccountFactoryIds(_data.account, (_ids) => setSavedFactoryIds(_ids ?? []));
      ThirdPartyAccountApi.getAccountSearchLimit(_data.account, (_limits) => {
        const limit = _limits?.find((item) => item.account === _data.account) ?? _limits?.[0];
        setHasSearchLimit(!!limit);
        setFormValues((prev) => ({ ...prev, ...toSearchLimitFormValues(limit) }));
      });
      ThirdPartyAccountApi.getAccountApiColumns(_data.account, (_list) => {
        const selection: ApiColumnSelection = {};
        (_list ?? []).forEach((item) => {
          selection[item.api_path] = item.column_names ?? [];
        });
        setSavedApiColumns(selection);
        setApiColumnSelection(selection);
      });
    }
  }, []);

  useEffect(() => {
    if (!id) return;
    ThirdPartyAccountApi.getAccountById({ id }, setAccountData);
  }, [id, setAccountData]);

  useEffect(() => {
    FactoryApi.getAllFactories((_data) => setAllFactories(_data ?? []));
    ThirdPartyAccountApi.getApiColumns((_data) => setAllApiColumns(_data ?? []));
  }, []);

  // API 只回傳工廠ID, 需對照全部工廠清單補上廠別代碼與廠名
  useEffect(() => {
    setUserGroupData({
      factoryIds: savedFactoryIds.map((factoryId) => {
        const factory = allFactories.find((item) => item.id === factoryId);
        return {
          id: factoryId,
          code: factory ? `${factory.factoryCode}${factory.branchCode}` : '',
          name: factory?.factoryName ?? `${factoryId}`,
        };
      }),
    });
  }, [savedFactoryIds, allFactories]);

  const handleGoBack = () => {
    navigate('/UsersManagePage/ThirdPartyAccountManagement');
  };

  // 將勾選內容有異動的API整理成陣列, 以 PUT 一次儲存全部異動, 完成後回傳錯誤訊息 (無錯誤為 undefined)
  const saveApiColumns = (account: string, onDone: (errorMessage?: string) => void) => {
    const requests = allApiColumns
      .filter((api) => !isSameColumns(savedApiColumns[api.api_path], apiColumnSelection[api.api_path]))
      .map((api) => ({ account, api_path: api.api_path, column_names: apiColumnSelection[api.api_path] ?? [] }));

    // 後端不接受空陣列, 沒有異動時不需呼叫
    if (requests.length === 0) {
      onDone();
      return;
    }

    ThirdPartyAccountApi.updateAccountApiColumns(
      requests,
      () => onDone(),
      (res) => onDone(res?.message ?? i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.API_COLUMN_UPDATE_FAILED')),
    );
  };

  const handleSubmit = (values: ThirdPartyAccountFormValues) => {
    if (!id) return;

    ThirdPartyAccountApi.updateAccount(
      {
        id,
        account: values.account,
        state,
        accountName: values.accountName,
        description: values.description,
        extraData: {
          availableDays: Number(values.availableDays),
        },
      },
      () => {
        // 儲存後重新取得資料，讓修改時間、限制搜尋資料日期與廠的關聯等欄位同步更新
        const reload = () => ThirdPartyAccountApi.getAccountById({ id }, setAccountData);

        // 帳號儲存成功後, 再以 PUT 儲存限制搜尋資料日期 (尚無設定時以 POST 新增)
        const saveSearchLimit = hasSearchLimit
          ? ThirdPartyAccountApi.updateAccountSearchLimit
          : ThirdPartyAccountApi.createAccountSearchLimit;
        saveSearchLimit(
          toSearchLimitRequest(values),
          () => {
            // 再以 PUT 將廠的關聯一次更新成目前清單
            ThirdPartyAccountApi.updateAccountFactories(
              {
                account: values.account,
                factory_ids: userGroupData.factoryIds.map((item) => Number(item.id)),
              },
              () => {
                // 最後儲存API回傳資料欄位的關聯
                saveApiColumns(values.account, (errorMessage) => {
                  setAlertText(errorMessage ?? i18T('GLOBAL.UPDATE_SUCCESS'));
                  setIsShowAlert(true);
                  reload();
                });
              },
              (res) => {
                setAlertText(
                  res?.message ?? i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.FACTORY_UPDATE_FAILED'),
                );
                setIsShowAlert(true);
                reload();
              },
            );
          },
          (res) => {
            setAlertText(
              res?.message ?? i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.SEARCH_LIMIT_UPDATE_FAILED'),
            );
            setIsShowAlert(true);
            reload();
          },
        );
      },
      (res) => {
        setAlertText(res?.message ?? i18T('GLOBAL.ERROR', { text: i18T('GLOBAL.SAVE') }));
        setIsShowAlert(true);
      },
    );
  };

  const handleRegenerateToken = () => {
    if (!formValues.account) return;

    setTokenLoading(true);
    ThirdPartyAccountApi.regenerateToken(
      { account: formValues.account },
      (_data) => {
        setToken(_data?.token ?? '');
        setAlertText(i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.REGENERATE_KEY_SUCCESS'));
        setIsShowAlert(true);
      },
      (res) => {
        setAlertText(res?.message ?? i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.REGENERATE_KEY_FAILED'));
        setIsShowAlert(true);
      },
      () => setTokenLoading(false),
    );
  };

  const handleDeleteFactory = (row: GroupData) => {
    setUserGroupData({
      factoryIds: userGroupData.factoryIds.filter((item) => item.id.toString() !== row.id.toString()),
    });
  };

  return (
    <>
      <YatungPage
        title={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.EDIT_ROUTE_TITLE')}
        contentBgColor="#FFF"
        body={
          <Formik
            initialValues={formValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {(formik) => (
              <Form>
                <Stack width="100%">
                  <Grid container item xs={12} justifyContent={'space-between'}>
                    <Grid container item xs={true}></Grid>
                    <Grid container item xs={true} gap={4} justifyContent={'center'} sx={{ mr: 3, my: 1 }}>
                      <ThirdPartyAccountFormActions onSubmit={formik.handleSubmit} onGoBack={handleGoBack} />
                    </Grid>
                  </Grid>
                  <Stack direction="row" alignItems="flex-start" gap={4}>
                    <Stack sx={{ flexShrink: 0 }}>
                      <Stack direction="column" justifyContent="space-evenly" sx={{ ml: 2 }}>
                        <Typography sx={{ fontWeight: 400 }}>
                          {i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.CREATE_TIME') +
                            '：' +
                            accountInfo.createDt}
                        </Typography>
                        <Typography>
                          {i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.UPDATE_TIME') +
                            '：' +
                            accountInfo.updateDt}
                        </Typography>
                        <Stack direction="row">
                          <Typography>{i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.STATE') + '：'}</Typography>
                          <Typography color={state === '1' ? '#00D03A' : 'red'}>
                            {state === '1'
                              ? i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.ENABLE')
                              : i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.DISABLED')}
                          </Typography>
                        </Stack>
                        <StateSwitch state={state} onChange={setState} />
                      </Stack>

                      <Stack direction="row" sx={{ mt: 3 }}>
                        <EditAccountLabel
                          label={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.ACCOUNT')}
                          isStar={true}
                        />
                        <YatungInput
                          value={formik.values.account}
                          disabled={true}
                          setInputProps={{ readOnly: true }}
                          sx={{ width: '450px', minWidth: '450px' }}
                        />
                      </Stack>
                      <Stack direction="row" sx={{ mt: 3 }}>
                        <EditAccountLabel
                          label={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.ACCOUNT_NAME')}
                          isStar={true}
                        />
                        <EditAccountField target="accountName" />
                      </Stack>
                      <Stack direction="row" sx={{ mt: 3 }}>
                        <EditAccountLabel label={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.DESCRIPTION')} />
                        <EditAccountField target="description" />
                      </Stack>
                      <Stack direction="row" sx={{ mt: 3 }}>
                        <EditAccountLabel
                          label={i18T('USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT.AVAILABLE_DAYS')}
                          isStar={true}
                        />
                        <EditAccountField target="availableDays" />
                      </Stack>
                      <ThirdPartyAccountSearchLimitFields />
                      <Stack direction="row" sx={{ mt: 3 }}>
                        <EditAccountLabel label={i18T('USERSMANAGEMENU.EDIT_ACCOUNT.FACTORY')} />
                        <EditAccountMiniTable
                          onCreate={() => setIsFactoryCreateOpen(true)}
                          onDelete={handleDeleteFactory}
                          codeTitle={i18T('USERSMANAGEMENU.EDIT_ACCOUNT.SITE_CODE')}
                          data={userGroupData.factoryIds}
                          isDisabled={false}
                        />
                      </Stack>
                    </Stack>
                    <Box sx={{ flex: 1, minWidth: 0, mt: 3, mr: 3 }}>
                      <ThirdPartyApiColumnSettings
                        apiColumns={allApiColumns}
                        selectedColumns={apiColumnSelection}
                        onChange={(apiPath, columnNames) =>
                          setApiColumnSelection((prev) => ({ ...prev, [apiPath]: columnNames }))
                        }
                      />
                    </Box>
                  </Stack>
                </Stack>
                <Stack sx={{ mt: 6, mb: 3 }}>
                  <YatungDivider />
                </Stack>
                <ThirdPartyKeyManagementTable
                  token={token}
                  loading={tokenLoading}
                  onRegenerate={handleRegenerateToken}
                />
              </Form>
            )}
          </Formik>
        }
      />
      {/* 每次開啟時重新掛載, 讓選取狀態從空白開始 */}
      {isFactoryCreateOpen && (
        <EditAccountCreateCard
          isOpen={isFactoryCreateOpen}
          value={1}
          handleGoBackButtonClick={() => setIsFactoryCreateOpen(false)}
          userGroupData={userGroupData}
          setUserGroupData={setUserGroupData}
        />
      )}
      <YatungConfirmAlert
        headerText={i18T('GLOBAL.SAVE')}
        isShowAlert={isShowAlert}
        onClose={() => setIsShowAlert(false)}
        alertText={alertText}
      />
    </>
  );
}
